import { useEffect, useMemo, useRef, useState } from "react";
import { useRecoilValue } from "recoil";

import {
  useApplication,
  useCustomQueryParams,
  usePageContext,
} from "@ryde/redux";
import {
  checkIsAllInit,
  companyIdSelector,
  finalSchemaInitStateSF,
  getLastFinalSchemaLocalStore,
  gridControlsSF,
  hasKeys,
  isViewLoadedSF,
  loadersEditFormDataSF,
  Log,
  NO_GROUPING_QS_VALUE,
  tableColumnsOrderStateSF,
  tableColumnsSortingSF,
  tableGroupingSF,
  unique,
  updateFinalSchemaLocalStore,
  viewChangesApplyingAtom,
  type IColumnDef,
  type IExtendableDto,
} from "@ryde/shared";

import {
  columnsOrderResolver,
  getFixedColumnsNamesFromTableDef,
} from "../table-defs";
import { useDefaultTableGrouping } from "./use-default-table-grouping";

export const useTableWrapper = ({
  tableDefs,
}: {
  tableDefs: IColumnDef<IExtendableDto, unknown>[];
}) => {
  const { entity } = usePageContext();
  const companyId = useRecoilValue(companyIdSelector);
  const { setTableSortingQsByValue, setTableGroupingQsByValue } =
    useCustomQueryParams();
  const { isAdminApp } = useApplication();
  const [isTableReady, setIsTableReady] = useState(false);

  const columnsSorting = useRecoilValue(tableColumnsSortingSF(entity));
  const columnsGrouping = useRecoilValue(tableGroupingSF(entity));
  const columnOrder = useRecoilValue(tableColumnsOrderStateSF(entity));

  const finalSchema = getLastFinalSchemaLocalStore(companyId ?? 0, entity);
  const isViewLoaded = useRecoilValue(isViewLoadedSF(entity));
  const finalSchemaInitState = useRecoilValue(finalSchemaInitStateSF(entity));
  const isViewApplying = useRecoilValue(viewChangesApplyingAtom);
  const gridControls = useRecoilValue(gridControlsSF(entity));

  const {
    groupingComplicatedInit,
    handleInitialColumnVisibility,
    handleInitialColumnSizing,
    handleInitialColumnSorting,
    setColumnOrderChecked,
  } = useDefaultTableGrouping();

  const fixedColumnNames = useMemo(() => {
    return getFixedColumnsNamesFromTableDef(tableDefs);
  }, [tableDefs]);

  const isViewChangingInProcess = useRecoilValue(viewChangesApplyingAtom);
  const isCrudLoading = useRecoilValue(loadersEditFormDataSF(entity));

  // initial order
  useEffect(() => {
    if (!checkIsAllInit(finalSchemaInitState) || !tableDefs.length) {
      return;
    }

    if (columnOrder?.length) {
      return;
    }

    const columnsOrderResolved = columnsOrderResolver<IExtendableDto>({
      finalSchemaOrder: finalSchema?.columnsOrder,
      columnsDef: tableDefs,
    });
    const normalizedColumnOrder = unique([
      ...fixedColumnNames.fixedColumns,
      ...columnsOrderResolved,
      ...fixedColumnNames.lastColumns,
    ]);

    setColumnOrderChecked(normalizedColumnOrder);
  }, [
    columnOrder?.length,
    finalSchema?.columnsOrder,
    finalSchemaInitState,
    fixedColumnNames.fixedColumns,
    fixedColumnNames.lastColumns,
    isViewLoaded,
    setColumnOrderChecked,
    tableDefs,
  ]);

  const isInitGridSchemaRef = useRef(false);
  // initial grouping, visibility, sorting
  useEffect(() => {
    if (isInitGridSchemaRef.current) {
      return;
    }

    if (!checkIsAllInit(finalSchemaInitState)) {
      return;
    }

    Log.debug("[Grid Schema Initialized]");

    groupingComplicatedInit();
    handleInitialColumnVisibility();
    handleInitialColumnSizing();
    handleInitialColumnSorting();
    isInitGridSchemaRef.current = true;
  }, [
    groupingComplicatedInit,
    handleInitialColumnVisibility,
    handleInitialColumnSizing,
    finalSchemaInitState,
    handleInitialColumnSorting,
  ]);

  // column grouping watcher
  useEffect(() => {
    if (!isInitGridSchemaRef.current) {
      return;
    }

    Log.debug("[Grid Schema Watcher] grouping changed:", columnsGrouping);

    if (
      columnsGrouping?.length &&
      columnsGrouping[0] !== undefined &&
      columnsGrouping[0] !== NO_GROUPING_QS_VALUE
    ) {
      setTableGroupingQsByValue(columnsGrouping);

      if (companyId) {
        updateFinalSchemaLocalStore({
          companyId,
          page: entity,
          finalSchema: { columnsGrouping },
        });
      }
    } else if (
      columnsGrouping?.length === 0 ||
      columnsGrouping[0] === NO_GROUPING_QS_VALUE
    ) {
      setTableGroupingQsByValue([]);

      if (companyId) {
        updateFinalSchemaLocalStore({
          companyId,
          page: entity,
          finalSchema: { columnsGrouping: [] },
        });
      }
    } else {
      setTableGroupingQsByValue(undefined);

      if (companyId) {
        updateFinalSchemaLocalStore({
          companyId,
          page: entity,
          finalSchema: { columnsGrouping: undefined },
        });
      }
    }
    // We don't want effect triggers on functions provided from other hooks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnsGrouping, companyId, entity]);

  // column sorting watcher
  useEffect(() => {
    if (!isInitGridSchemaRef.current) {
      return;
    }

    Log.debug("[Grid Schema Watcher] sorting changed:", columnsSorting);

    if (hasKeys(columnsSorting)) {
      setTableSortingQsByValue(columnsSorting);

      if (companyId) {
        updateFinalSchemaLocalStore({
          companyId,
          page: entity,
          finalSchema: { columnsSorting },
        });
      }
    } else {
      setTableSortingQsByValue(undefined);

      if (companyId) {
        updateFinalSchemaLocalStore({
          companyId,
          page: entity,
          finalSchema: { columnsSorting: undefined },
        });
      }
    }
    // We don't want effect triggers on functions provided from other hooks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnsSorting, companyId, entity]);

  useEffect(() => {
    const areDataLoading =
      !isViewChangingInProcess && !isViewApplying && !isCrudLoading;

    if (isAdminApp && tableDefs?.length && areDataLoading) {
      setIsTableReady(true);
    } else if (
      !isAdminApp &&
      tableDefs?.length &&
      isViewLoaded &&
      gridControls?.length &&
      columnOrder?.length &&
      areDataLoading
    ) {
      setIsTableReady(true);
    }
  }, [
    columnOrder?.length,
    gridControls?.length,
    isAdminApp,
    isCrudLoading,
    isViewApplying,
    isViewChangingInProcess,
    isViewLoaded,
    tableDefs?.length,
  ]);

  useEffect(() => {
    return () => setIsTableReady(false);
  }, []);

  return {
    isTableReady,
    isViewApplying,
  };
};
